"use client";

import clsx from "clsx";
import { useEffect, useState } from "react";
import Icon from "@/components/library/Icon";

const VISIBLE_CODE_LINES = 3;
const CODE_LINE_HEIGHT = 28; // leading-7 = 28px

interface ScriptBuildingLoaderProps {
  /** Heading shown above the loader. */
  readonly title: string;
  /** Subtitle shown below the heading. */
  readonly subtitle: string;
  /** Step messages that tick through one at a time. */
  readonly messages: string[];
  /** Fake code lines that scroll in the dark panel. */
  readonly codeLines: string[];
  /** Labels for the three progress bars. */
  readonly metricLabels: [string, string, string];
  /**
   * Badge label shown in the top-left of the card (e.g. "Live Build").
   * @default "Live Build"
   */
  readonly badgeLabel?: string;
  /**
   * When true, animation intervals are slowed (e.g. while reference files are processed).
   * @default false
   */
  readonly hasUploadedFiles?: boolean;
  /**
   * Number of leading steps already finished (e.g. file upload before script generation).
   * The loader holds on the next step until this count advances.
   * @default 0
   */
  readonly leadingCompletedSteps?: number;
}

const FILE_UPLOAD_TIMING_MULTIPLIER = 4;

/**
 * Futuristic "live build" loading animation used across onboarding steps.
 *
 * Displays a scrolling code panel, three progress bars, a pulsing status
 * message, and a stepper list of messages that complete one-by-one.
 *
 * The tick speed is derived from the number of messages so the animation
 * naturally fills the expected wait time.
 */
export function ScriptBuildingLoader({
  title,
  subtitle,
  messages,
  codeLines,
  metricLabels,
  badgeLabel = "Live Build",
  hasUploadedFiles = false,
  leadingCompletedSteps = 0,
}: ScriptBuildingLoaderProps) {
  // Guard against non-array values (e.g. i18n returnObjects returning a string before namespace loads)
  const safeMessages = Array.isArray(messages) ? messages : [messages].filter(Boolean);
  const safeCodeLines = Array.isArray(codeLines) ? codeLines : [];
  const safeMetricLabels: [string, string, string] = Array.isArray(metricLabels)
    ? metricLabels
    : ["", "", ""];

  const [activeMessageIndex, setActiveMessageIndex] = useState(0);
  const [codeLineOffset, setCodeLineOffset] = useState(0);

  const hasMessages = safeMessages.length > 0;
  const timingMultiplier = hasUploadedFiles ? FILE_UPLOAD_TIMING_MULTIPLIER : 1;

  const completedLeadingSteps = Math.min(
    Math.max(leadingCompletedSteps, 0),
    Math.max(safeMessages.length - 1, 0)
  );

  // Advance past leading steps when the parent marks them complete (e.g. uploads finished).
  useEffect(() => {
    setActiveMessageIndex((prev) => Math.max(prev, completedLeadingSteps));
  }, [completedLeadingSteps]);

  // Step message ticker — advances every ~3.5 s for ≤5 messages, faster for fewer
  const messageInterval = (safeMessages.length <= 3 ? 2500 : 3500) * timingMultiplier;
  useEffect(() => {
    if (activeMessageIndex >= safeMessages.length - 1) return;
    if (activeMessageIndex < completedLeadingSteps) return;

    const timer = setInterval(() => {
      setActiveMessageIndex((prev) => (prev + 1 >= safeMessages.length ? prev : prev + 1));
    }, messageInterval);

    return () => clearInterval(timer);
  }, [activeMessageIndex, completedLeadingSteps, safeMessages.length, messageInterval]);

  // Code line scroll — faster than messages by default; spread across the full
  // message timeline when files are uploaded so the top panel does not finish early.
  const maxCodeOffset = Math.max(safeCodeLines.length - VISIBLE_CODE_LINES, 0);
  const defaultCodeInterval = (safeMessages.length <= 3 ? 1000 : 1400) * timingMultiplier;
  const totalMessageTimelineMs =
    hasMessages && safeMessages.length > 1
      ? messageInterval * (safeMessages.length - 1)
      : messageInterval;
  const codeInterval =
    hasUploadedFiles && maxCodeOffset > 0 && hasMessages
      ? totalMessageTimelineMs / maxCodeOffset
      : defaultCodeInterval;
  const motionDurationMs = 700 * timingMultiplier;
  useEffect(() => {
    const maxOffset = safeCodeLines.length - VISIBLE_CODE_LINES;
    if (maxOffset <= 0 || codeLineOffset >= maxOffset) return;

    const timer = setInterval(() => {
      setCodeLineOffset((prev) => (prev >= maxOffset ? prev : prev + 1));
    }, codeInterval);

    return () => clearInterval(timer);
  }, [codeLineOffset, safeCodeLines.length, codeInterval]);

  const codeTranslateY = -(codeLineOffset * CODE_LINE_HEIGHT);
  const progressStepCount = Math.max(safeMessages.length - 1, 1);
  const progress = hasMessages
    ? (activeMessageIndex / progressStepCount) * 100
    : 0;
  const confidence = hasMessages
    ? 30 + (activeMessageIndex / progressStepCount) * 65
    : 30;

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-8">
        <div className="flex h-full items-center justify-center">
          <div className="w-full max-w-2xl space-y-6">
            {/* Header */}
            <div className="space-y-2 text-center">
              <h2 className="text-2xl font-semibold text-foreground">{title}</h2>
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            </div>

            {/* Card */}
            <div className="rounded-xl border border-border bg-panel p-4 space-y-4">
              {/* Badge row */}
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                    {badgeLabel}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-primary animate-bounce [animation-delay:-0.3s]" />
                  <span className="w-2 h-2 rounded-full bg-primary animate-bounce [animation-delay:-0.15s]" />
                  <span className="w-2 h-2 rounded-full bg-primary animate-bounce" />
                </div>
              </div>

              {/* Code panel + progress bars */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Code panel — fixed dark terminal surface (bg-900 inverts in dark theme) */}
                <div className="relative h-[148px] overflow-hidden rounded-lg bg-[rgb(20,25,30)] px-3 ring-1 ring-border/50">
                  <div
                    className="ease-in-out pt-2 transition-transform"
                    style={{
                      transform: `translateY(${codeTranslateY}px)`,
                      transitionDuration: `${motionDurationMs}ms`,
                    }}
                  >
                    {safeCodeLines.map((line, index) => {
                      const isInView =
                        index >= codeLineOffset && index < codeLineOffset + VISIBLE_CODE_LINES;
                      const isBottomVisible = index === codeLineOffset + VISIBLE_CODE_LINES - 1;
                      const isAboveView = index < codeLineOffset;
                      return (
                        <p
                          key={`L${index}-${line}`}
                          className={clsx(
                            "font-mono text-[11px] leading-7 transition-colors duration-500 whitespace-nowrap",
                            isAboveView && "text-emerald-400/40",
                            isInView && isBottomVisible && "text-emerald-300 animate-pulse",
                            isInView && !isBottomVisible && "text-emerald-400/80",
                            !isInView && !isAboveView && "text-emerald-500/25"
                          )}
                        >
                          {line}
                        </p>
                      );
                    })}
                  </div>
                </div>

                {/* Progress bars */}
                <div className="space-y-2 flex flex-col justify-center min-h-[120px]">
                  {[
                    { label: safeMetricLabels[0], value: `${Math.round(progress)}%` },
                    { label: safeMetricLabels[1], value: `${Math.round(progress)}%` },
                    { label: safeMetricLabels[2], value: `${Math.round(confidence)}%` },
                  ].map((metric) => (
                    <div
                      key={metric.label}
                      className="rounded-md border border-border bg-panel_secondary px-3 py-2"
                    >
                      <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                        <span>{metric.label}</span>
                        <span className="text-primary font-medium">{metric.value}</span>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-primary transition-all ease-in-out"
                          style={{
                            width: metric.value,
                            transitionDuration: `${motionDurationMs}ms`,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pulsing current-step message */}
              <p className="text-sm text-primary font-medium animate-pulse">
                {safeMessages[activeMessageIndex]}
              </p>

              {/* Stepper */}
              <ul className="space-y-2 border-t border-border pt-2">
                {safeMessages.map((message, index) => {
                  const isActive = index === activeMessageIndex;
                  const isCompleted = index < activeMessageIndex;
                  return (
                    <li
                      key={`M${index}-${message}`}
                      className={clsx(
                        "text-sm transition-colors flex items-center gap-2",
                        isCompleted && "text-emerald-600 dark:text-emerald-400",
                        isActive && "text-primary",
                        !isCompleted && !isActive && "text-muted-foreground"
                      )}
                    >
                      {isCompleted ? (
                        <Icon icon="check" className="w-4 h-4 flex-shrink-0" />
                      ) : (
                        <span className="text-base leading-none flex-shrink-0">•</span>
                      )}
                      {message}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
